import { OverlayModule } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { leaveLink } from '../../../functions/claim/leaveLink';
import { leaveLob } from '../../../functions/claim/leaveLinkLob';
import { liability } from '../../../functions/claim/liability';
import { ClaimRow } from '../../../models/api/view/claim-view/claim-row';
import { Action } from '../../../models/core/action';
import { pageSettings } from '../../../models/core/security/page-settings';
import { PageToken } from '../../../models/core/security/page-token';
import { SecurityService } from '../../../services/security/security.service';
import { BaseComponent } from '../../base/base.component';
import { ClaimMenuItemComponent } from './claim-menu-item.component';

@Component({
  selector: 'app-claim-menu',
  imports: [CommonModule, OverlayModule, ClaimMenuItemComponent],
  template: `
    <div class="tw-w-full tw-flex tw-flex-wrap" role="menubar">
      @for (item of items; track item) {
        @if (item.authorized) {
          <app-claim-menu-item [item]="item" />
        }
      }
    </div>
  `,
  styles: ``,
})
export class ClaimMenuComponent extends BaseComponent implements OnInit {
  private security = inject(SecurityService);

  items: Action[] = [];
  mtTxLawAuth = false;

  ngOnInit(): void {
    this.buildMenu();
    // Rebuild menu when resolver updates claim (handles browser back/forward)
    this.dispose.subs = this.activatedRoute.data.subscribe((data) => {
      if (data['claim']) {
        this.buildMenu();
      }
    });
  }

  add(parent: Action, pageToken: PageToken, title: string = null) {
    if (this.security.pageAuthorize(pageToken)) {
      const settings = pageSettings.get(pageToken);
      (parent?.items ?? this.items).push({
        label: title ?? settings.title,
        authorized: true,
        routerLink: settings.path,
        items: [],
      });
    }
  }

  addSidekickSummaryMenu(parent: Action) {
    if (this.security.sidekickSummaryMenuAuthorize()) {
      this.add(parent, 'sidekick-summary-page');
    }
  }

  buildLaMenu() {
    const claim = this.view.currentClaim();
    const isLeaveLink = leaveLink(claim?.recordSource);
    const details = this.addItem('Details');
    this.add(details, 'absence-page');
    this.add(details, 'leave-calendar-page');
    this.add(details, 'certifications-page');
    this.add(details, 'eligibility-page');
    this.add(details, 'address-book-page');
    this.add(details, 'custom-fields-page');
    const documentation = this.addItem('Documentation');
    this.add(documentation, 'notes-page');
    if (!isLeaveLink) {
      this.add(documentation, 'examiner-diary-page');
    }
    this.add(documentation, 'documents-page');
    if (isLeaveLink) {
      this.add(documentation, 'correspondence-page');
    }
    const hrData = this.addItem(
      'HR data',
      null,
      this.session.authorize('EmployeeDetail'),
    );
    this.add(hrData, 'employee-detail-page');
    this.add(hrData, 'job-history-page');
    this.add(hrData, 'hr-contacts-page');
    this.add(hrData, 'dependents-page');

    this.add(null, 'document-tasks-page');
  }

  buildAdMenu() {
    const details = this.addItem('Details');
    this.add(details, 'job-functions-page');
    this.add(details, 'interactions-page');
    this.add(details, 'restrictions-page');
    this.add(details, 'accommodations-page');
    this.add(details, 'milestones-page');
    const documentation = this.addItem('Documentation');
    this.add(documentation, 'notes-page');
    this.add(documentation, 'examiner-diary-page');
    this.add(documentation, 'documents-page');
    const hrData = this.addItem(
      'HR data',
      null,
      this.session.authorize('EmployeeDetail'),
    );
    this.add(hrData, 'employee-detail-page');
    this.add(hrData, 'hr-contacts-page');

    this.add(null, 'document-tasks-page');
  }

  buildLiabilityMenu(claim: ClaimRow) {
    const details = this.addItem('Details');
    this.addSidekickSummaryMenu(details);
    switch (claim.lineOfBusiness) {
      case 'GL':
        this.add(details, 'general-liability-page');
        break;
      case 'PR':
        this.add(details, 'property-page');
        break;
      case 'AU':
        this.add(details, 'auto-liability-page');
        break;
      default:
        break;
    }

    if (claim.lineOfBusiness !== 'PR') {
      this.add(details, 'medical-page');
    }

    this.add(details, 'medicare-page');
    this.add(details, 'case-management-page');
    this.add(details, 'legal-page');
    this.add(details, 'vendor-referral-page');
    this.add(details, 'address-book-page');
    this.add(details, 'custom-fields-page');
    this.add(details, 'plan-summary-page');
    this.add(details, 'policy-details-page');
    if (['AU', 'GL'].includes(claim.lineOfBusiness)) {
      this.add(details, 'tender-page');
    }

    this.buildFinancialMenu({
      includeOverpayments: false,
      includeOffsets: true,
    });
    const documentation = this.addItem('Documentation');
    this.add(documentation, 'notes-page');
    this.add(documentation, 'examiner-diary-page');
    this.add(documentation, 'documents-page');
    this.add(documentation, 'index-system-page');
    this.add(documentation, 'service-instructions-page');
    this.add(null, 'document-tasks-page');
  }

  buildAcMenu() {
    const details = this.addItem('Details');
    this.add(details, 'disability-page', 'Accommodations');
    this.add(details, 'time-tracking-page');
    this.add(details, 'address-book-page');
    this.add(details, 'custom-fields-page');
    const documentation = this.addItem('Documentation');
    this.add(documentation, 'notes-page');
    this.add(documentation, 'examiner-diary-page');
    this.add(documentation, 'documents-page');
    const hrData = this.addItem(
      'HR data',
      null,
      this.session.authorize('EmployeeDetail'),
    );
    this.add(hrData, 'employee-detail-page');
    this.add(hrData, 'job-history-page');
    this.add(hrData, 'hr-contacts-page');

    this.add(null, 'document-tasks-page');
  }

  buildWcMenu() {
    const details = this.addItem('Details');
    if (!this.mtTxLawAuth) {
      this.addSidekickSummaryMenu(details);
    }
    this.add(details, 'workers-compensation-page');
    this.add(details, 'claimant-page');
    this.add(details, 'medical-page');
    this.add(details, 'medicare-page');
    this.add(details, 'case-management-page');
    this.add(details, 'time-tracking-page');
    this.add(details, 'legal-page');
    this.add(details, 'appeals-page');
    this.add(details, 'vendor-referral-page');
    this.add(details, 'address-book-page');
    this.add(details, 'custom-fields-page');
    this.add(details, 'plan-summary-page');
    this.add(details, 'policy-details-page');
    this.buildFinancialMenu({ includeOverpayments: true });
    const documentation = this.addItem('Documentation');
    this.add(documentation, 'notes-page');
    this.add(documentation, 'examiner-diary-page');
    this.add(documentation, 'documents-page');
    this.add(documentation, 'index-system-page');
    this.add(documentation, 'edi-page');
    this.add(documentation, 'service-instructions-page');
    if (this.view.authorize('EmployeeDetail')) {
      const hrData = this.addItem('HR data');
      this.add(hrData, 'employee-detail-page');
      this.add(hrData, 'job-history-page');
      this.add(hrData, 'hr-contacts-page');
      this.add(hrData, 'deductions-page');
      this.add(hrData, 'dependents-page');
      this.add(hrData, 'payroll-page');
      this.add(hrData, 'accruals-page');
      this.add(hrData, 'earnings-page');
    }

    this.add(null, 'document-tasks-page');
  }

  buildDsMenu() {
    const details = this.addItem('Details');
    this.add(details, 'disability-page');
    this.add(details, 'benefit-plan-page');
    this.add(details, 'medical-page');
    this.add(details, 'time-tracking-page');
    this.add(details, 'legal-page');
    this.add(details, 'appeals-page');
    this.add(details, 'address-book-page');
    this.add(details, 'custom-fields-page');
    const financials = this.addItem('Financials');
    this.add(financials, 'payment-history-page');
    this.add(financials, 'recoveries-page');
    this.add(financials, 'overpayments-page');
    this.add(financials, 'offsets-page');
    const documentation = this.addItem('Documentation');
    this.add(documentation, 'notes-page');
    this.add(documentation, 'examiner-diary-page');
    this.add(documentation, 'documents-page');
    const hrData = this.addItem(
      'HR data',
      null,
      this.view.authorize('EmployeeDetail'),
    );
    this.add(hrData, 'employee-detail-page');
    this.add(hrData, 'job-history-page');
    this.add(hrData, 'hr-contacts-page');
    this.add(hrData, 'deductions-page');
    this.add(hrData, 'dependents-page');
    this.add(hrData, 'payroll-page');
    this.add(hrData, 'accruals-page');
    this.add(hrData, 'earnings-page');

    this.add(null, 'document-tasks-page');
  }

  buildMenu() {
    this.items = [];
    const claim = this.view.currentClaim();
    this.mtTxLawAuth = this.view.authorize('MTTXLaw');
    this.addItem('Summary', '');
    if (leaveLob(claim.lineOfBusiness)) {
      this.buildLaMenu();
    } else if (claim.lineOfBusiness === 'AD') {
      this.buildAdMenu();
    } else if (claim.lineOfBusiness === 'WC') {
      this.buildWcMenu();
    } else if (liability(claim.lineOfBusiness)) {
      this.buildLiabilityMenu(claim);
    } else if (claim.lineOfBusiness === 'DS') {
      if (claim.coverageCode === 'AC') {
        this.buildAcMenu();
        const docIndex = this.items.findIndex(
          (item) =>
            item.label === 'Documentation' &&
            !item.items.some((child) => child.authorized),
        );

        if (docIndex !== -1) {
          this.removeItem(docIndex);
        }
      } else {
        this.buildDsMenu();
      }
    }

    this.remmoveEmptyMenus();
  }

  remmoveEmptyMenus() {
    for (let index = this.items.length - 1; index >= 0; index--) {
      const item = this.items[index];
      if (item.items.length === 0 && item.routerLink === null) {
        this.removeItem(index);
      }
    }
  }

  addItem(label: string, routerLink: string = null, authorized = true): Action {
    const length = this.items.push({
      label,
      authorized,
      routerLink,
      items: [],
    });

    return this.items[length - 1];
  }

  removeItem(index: number): void {
    this.items.splice(index, 1);
  }

  /**
   * Builds the Financial menu. For MT claims, only Recoveries is available.
   */
  buildFinancialMenu(
    options: { includeOverpayments?: boolean; includeOffsets?: boolean } = {
      includeOverpayments: true,
      includeOffsets: true,
    },
  ): void {
    // Show Financial menu if user has access to any financial page
    // For MT claims, only Recoveries is available
    const financials = this.addItem('Financials');
    this.add(financials, 'overview-page');
    this.add(financials, 'payment-history-page');
    this.add(financials, 'invoice-list-page');
    this.add(financials, 'reserves-page');
    this.add(financials, 'reimbursements-page');
    this.add(financials, 'recoveries-page');
    const includeOverpayments =
      options.includeOverpayments !== undefined
        ? options.includeOverpayments
        : true;
    const includeOffsets =
      options.includeOffsets !== undefined ? options.includeOffsets : true;

    if (includeOverpayments) {
      this.add(financials, 'overpayments-page');
    }
    if (includeOffsets) {
      this.add(financials, 'offsets-page');
    }
  }
}
